import { Box, Typography, Divider, Button, IconButton, Tooltip } from '@mui/material';
import type { FC } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { RuleFilter } from '../types';
import FilterForm from './FilterForm';
import useFilterStore from '../hooks/useFilterStore';
import useRulePageMode from '../hooks/useRulePageMode';

interface IProps {
  groupIndex: number;
  group: RuleFilter[];
}

const RuleEditFilterGroup: FC<IProps> = observer(({ groupIndex, group }) => {
  const { isRead } = useRulePageMode();
  const { onShowInfoAlert } = useEnqueueSnackbar();
  const filterStore = useFilterStore();
  const addFilterGroupButtonRef = useRef<HTMLButtonElement>(null);

  const handleDuplicateFilterGroup = () => {
    filterStore.duplicateFilterGroup(groupIndex);
    onShowInfoAlert('Filter group copied');
    if (addFilterGroupButtonRef.current) {
      addFilterGroupButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const hadnleRemoveFilterGroup = () => {
    const result = filterStore.removeFilterGroup(groupIndex);
    if (result) {
      onShowInfoAlert('Filter group deleted');
    } else {
      onShowInfoAlert(`You can't delete last filter group`);
    }
  };

  const handleAddFilter = () => {
    const isAdded = filterStore.addFilter(groupIndex);
    if (!isAdded) {
      onShowInfoAlert('Please finish editing last filter before adding new');
    }
  };

  return (
    <Box id={`fg-${groupIndex}`}>
      <Box sx={{ display: 'flex', justifyContent: isRead ? 'start' : 'space-between', alignItems: 'center', mb: 2 }}>
        <Box
          sx={(t) => ({
            p: 0.5,
            background: t.palette.primary.main,
            display: 'inline-flex',
            alignItems: 'center',
            color: 'white',
            borderRadius: 1,
          })}
        >
          <FilterListIcon fontSize="large" sx={{ fontSize: 24, mr: 1 }} />
          <Typography sx={(t) => ({ fontSize: t.typography.body2.fontSize })}>Filter group {groupIndex + 1}</Typography>
        </Box>
        {!isRead && (
          <Box>
            <Tooltip title="Duplicate filter group" placement="top" arrow>
              <IconButton onClick={handleDuplicateFilterGroup}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete filter group" placement="top" arrow>
              <IconButton onClick={hadnleRemoveFilterGroup}>
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '60%' }}>
        {group.map((f, index) => (
          <FilterForm key={f.field + index} index={index} groupIndex={groupIndex} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: isRead ? 3 : 2 }}>
        {!isRead && (
          <Button ref={addFilterGroupButtonRef} onClick={handleAddFilter}>
            <AddIcon sx={{ mr: 1 }} /> Add filter
          </Button>
        )}
        <Box sx={{ display: 'flex' }}>
          <InfoOutlinedIcon sx={(t) => ({ fontSize: 16, mr: 0.5, color: t.palette.text.secondary })} />
          <Typography color="text.secondary" variant="body2">
            Filter for &rdquo;NORULE&rdquo; is enabled by default
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mb: 1 }} />
    </Box>
  );
});

export default RuleEditFilterGroup;
